import { z } from "zod";
import { DataSource, normalizeDataSource } from "@/config/datasources";
import { logger } from "@/lib/logger";
import { formatDanishDate, parseSignatureDate } from "@/lib/timelineDates";
import type { Legislation } from "@/models/legislation/Legislation";
import {
  arvidLabelMap,
  domsdatabasenDocumentLabelMap,
  domsdatabasenLabelMap,
  eurLexLabelMap,
  parseEurLexTitle,
} from "@/utils/legislationMetadataUtils";
import { parseMetadataObject } from "@/utils/metadataValueResolver";

/**
 * What a printout of a Kilder document carries above the document text so the
 * reader can identify the source on paper (LAR-121).
 */
export type LegislationPrintHeader = {
  /** Citation-style identity, e.g. "LBK nr 1234 af 12/03/2019". */
  headline: string | null;
  /** Labelled facts shown under (or instead of) the headline, e.g. "Nummer: 9175". */
  details: string[];
};

/** A field that may be missing, null, a string or a number; anything else is dropped. */
const optionalText = z
  .union([z.string(), z.number()])
  .nullish()
  .catch(undefined);

/**
 * The few metadata fields the header reads, across all supported sources. Keys
 * are matched after normalisation to camelCase: the API camelCases the typed
 * `metadata` but passes the silver `metadataObject` JSON through verbatim, and
 * that JSON is snake_case for ARVID and Domsdatabasen (`journal_number`,
 * `court_case_number`) and PascalCase for EUR-Lex (`CelexNumber`).
 */
const printMetadataSchema = z.object({
  // Retsinformation
  shortName: optionalText,
  regulationNumber: optionalText,
  signatureDate: optionalText,
  yearOfIssue: optionalText,
  metadata: z
    .array(z.object({ displayName: optionalText, displayValue: optionalText }))
    .nullish()
    .catch(undefined),
  // ARVID
  journalNumber: optionalText,
  rulingOn: optionalText,
  // EUR-Lex
  celexNumber: optionalText,
  documentNumber: optionalText,
  year: optionalText,
  title: optionalText,
  // Domsdatabasen
  courtCaseNumber: optionalText,
  caseworkerCaseNumber: optionalText,
  documents: z
    .array(
      z.object({ documentType: optionalText, verdictDateTime: optionalText })
    )
    .nullish()
    .catch(undefined),
});

type PrintMetadata = z.infer<typeof printMetadataSchema>;

const nonBlank = (value: string | number | null | undefined): string | null => {
  if (value == null) return null;
  const text = String(value).trim();
  return text ? text : null;
};

/** Formats an ISO or Danish `DD/MM/YYYY` date the way legal citations write it. */
const legalDate = (
  value: string | number | null | undefined
): string | null => {
  const parsed = parseSignatureDate(nonBlank(value) ?? undefined);
  return parsed ? formatDanishDate(parsed) : null;
};

/** `journal_number` → `journalNumber`, `CelexNumber` → `celexNumber`. */
const toCamelKey = (key: string): string => {
  const snakeless = key.replace(/_([a-zA-Z0-9])/g, (_, next: string) =>
    next.toUpperCase()
  );
  return snakeless.charAt(0).toLowerCase() + snakeless.slice(1);
};

const normalizeKeys = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(normalizeKeys);
  if (value && typeof value === "object" && value.constructor === Object) {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, inner]) => [
        toCamelKey(key),
        normalizeKeys(inner),
      ])
    );
  }
  return value;
};

/**
 * Silver Retsinformation rows do not always carry the flat `regulationNumber` /
 * `signatureDate` fields; the retsinformation.dk display-item list is the
 * fallback and uses these exact labels.
 */
const RETSINFORMATION_DISPLAY_LABEL = {
  number: "Forskriftens nummer",
  signatureDate: "Dato for underskrift",
  yearOfIssue: "År for udstedelse",
} as const;

const retsinformationDisplayItem = (
  metadata: PrintMetadata,
  displayName: string
): string | null => {
  const item = metadata.metadata?.find(
    (entry) => nonBlank(entry.displayName) === displayName
  );
  return nonBlank(item?.displayValue);
};

const detailsOrNull = (details: string[]): LegislationPrintHeader | null =>
  details.length > 0 ? { headline: null, details } : null;

const buildRetsinformationHeader = (
  metadata: PrintMetadata
): LegislationPrintHeader | null => {
  const headline = nonBlank(metadata.shortName);
  // The shortName already reads "<type> nr <number> af <date>"; repeating the
  // parts under it would only clutter the page.
  if (headline) return { headline, details: [] };

  const number =
    nonBlank(metadata.regulationNumber) ??
    retsinformationDisplayItem(metadata, RETSINFORMATION_DISPLAY_LABEL.number);
  const signatureDate =
    legalDate(metadata.signatureDate) ??
    legalDate(
      retsinformationDisplayItem(
        metadata,
        RETSINFORMATION_DISPLAY_LABEL.signatureDate
      )
    );
  const yearOfIssue =
    nonBlank(metadata.yearOfIssue) ??
    retsinformationDisplayItem(
      metadata,
      RETSINFORMATION_DISPLAY_LABEL.yearOfIssue
    );

  const details: string[] = [];
  if (number) details.push(`Nummer: ${number}`);
  if (signatureDate) details.push(`Dato: ${signatureDate}`);
  else if (yearOfIssue) details.push(`Udgivelsesår: ${yearOfIssue}`);

  return detailsOrNull(details);
};

const buildArvidHeader = (
  metadata: PrintMetadata
): LegislationPrintHeader | null => {
  const details: string[] = [];
  const journalNumber = nonBlank(metadata.journalNumber);
  if (journalNumber) {
    details.push(`${arvidLabelMap.journalNumber}: ${journalNumber}`);
  }
  const rulingOn = legalDate(metadata.rulingOn);
  if (rulingOn) details.push(`${arvidLabelMap.rulingOn}: ${rulingOn}`);

  return detailsOrNull(details);
};

/**
 * `parseEurLexTitle` builds its date in local time and serialises it with
 * `toISOString()`, so the calendar day only survives a round trip through a
 * local-time `Date`; splitting the ISO string's date part would land a day early
 * anywhere east of UTC.
 */
const titleDate = (value: string | undefined): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : formatDanishDate(parsed);
};

/**
 * EUR-Lex silver carries the CELEX number and the act's own number and year, but
 * no document date; the title ("… (EU) 2016/679 af 27. april 2016 …") is the only
 * place the date lives, so it is parsed the same way the search results do it.
 */
const buildEurLexHeader = (
  metadata: PrintMetadata,
  title: string
): LegislationPrintHeader | null => {
  const parsedTitle = parseEurLexTitle(nonBlank(metadata.title) ?? title);

  const details: string[] = [];
  const celexNumber = nonBlank(metadata.celexNumber);
  if (celexNumber) {
    details.push(`${eurLexLabelMap.celexNumber}: ${celexNumber}`);
  }
  // The title yields the citation form ("2025/327"); silver's DocumentNumber is
  // only the sequence within the year ("0327"), so it is the fallback.
  const number = parsedTitle.number ?? nonBlank(metadata.documentNumber);
  if (number) details.push(`Nummer: ${number}`);
  const date = titleDate(parsedTitle.date);
  const year = nonBlank(metadata.year);
  if (date) details.push(`Dato: ${date}`);
  else if (year) details.push(`År: ${year}`);

  return detailsOrNull(details);
};

/**
 * A Domsdatabasen case bundles several documents (dom, kendelse, …). The verdict
 * date shown is the judgment's when one is present, otherwise the first dated
 * document's — mirroring the search-result summary.
 */
const domsdatabasenVerdictDate = (metadata: PrintMetadata): string | null => {
  const dated = (metadata.documents ?? []).filter((document) =>
    Boolean(nonBlank(document.verdictDateTime))
  );
  const judgment = dated.find((document) =>
    (nonBlank(document.documentType) ?? "").toLowerCase().includes("dom")
  );
  return legalDate((judgment ?? dated[0])?.verdictDateTime);
};

const buildDomsdatabasenHeader = (
  metadata: PrintMetadata
): LegislationPrintHeader | null => {
  const details: string[] = [];
  const courtCaseNumber = nonBlank(metadata.courtCaseNumber);
  const caseworkerCaseNumber = nonBlank(metadata.caseworkerCaseNumber);
  if (courtCaseNumber) {
    details.push(
      `${domsdatabasenLabelMap.courtCaseNumber}: ${courtCaseNumber}`
    );
  } else if (caseworkerCaseNumber) {
    details.push(
      `${domsdatabasenLabelMap.caseworkerCaseNumber}: ${caseworkerCaseNumber}`
    );
  }
  const verdictDate = domsdatabasenVerdictDate(metadata);
  if (verdictDate) {
    details.push(
      `${domsdatabasenDocumentLabelMap.verdictDateTime}: ${verdictDate}`
    );
  }

  return detailsOrNull(details);
};

const resolvePrintMetadata = (
  legislation: Legislation
): PrintMetadata | null => {
  const raw = parseMetadataObject(legislation);
  if (!raw) return null;
  const parsed = printMetadataSchema.safeParse(normalizeKeys(raw));
  return parsed.success ? parsed.data : null;
};

const buildHeaderForSource = (
  source: DataSource,
  metadata: PrintMetadata,
  legislation: Legislation
): LegislationPrintHeader | null => {
  switch (source) {
    case DataSource.Retsinformation:
      return buildRetsinformationHeader(metadata);
    case DataSource.Arvid:
      return buildArvidHeader(metadata);
    case DataSource.Eurlex:
      return buildEurLexHeader(metadata, legislation.title);
    case DataSource.Domsdatabasen:
      return buildDomsdatabasenHeader(metadata);
    default:
      return null;
  }
};

const SOURCES_WITH_PRINT_HEADER: ReadonlySet<DataSource> = new Set([
  DataSource.Retsinformation,
  DataSource.Arvid,
  DataSource.Eurlex,
  DataSource.Domsdatabasen,
]);

/**
 * Builds the number/date header printed above a Kilder document. Sources without
 * a defined header (everything but Retsinformation, ARVID, EUR-Lex and
 * Domsdatabasen today) yield null. A supported document whose metadata carries
 * none of the identifying fields also yields null, and is logged: the printout
 * then silently lacks its header.
 */
export const buildLegislationPrintHeader = (
  legislation: Legislation | null | undefined
): LegislationPrintHeader | null => {
  if (!legislation) return null;

  const source = normalizeDataSource(legislation.datasource);
  if (!source || !SOURCES_WITH_PRINT_HEADER.has(source)) return null;

  const metadata = resolvePrintMetadata(legislation);
  const header = metadata
    ? buildHeaderForSource(source, metadata, legislation)
    : null;

  if (!header) {
    logger.warn("Kilder print header has no number or date to show", {
      operation: "buildLegislationPrintHeader",
      externalId: legislation.externalId,
      datasource: legislation.datasource,
    });
  }

  return header;
};
